import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Group,
  LoadingOverlay,
  NumberInput,
  Stack,
  Switch,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifySuccess } from "../../utils/notify";
import { IconAlertCircle } from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import usePermissions from "../../hooks/usePermissions";
import {
  SettingsPageLayout,
  SettingsPageHeader,
  SettingsSection,
  SettingsField,
  SettingsTabShell,
  SettingsEditButton,
} from "./common";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { BusinessInfo, SalonContextType } from "../../types/context";
import { GiftCardConfig } from "../../types/giftCards";

/** The universal floor for a gift card minimum, enforced across every salon. */
const UNIVERSAL_MIN_AMOUNT = 1;

const GIFT_CARDS_SUBTITLE =
  "Configure the stored-value gift cards your salon sells. The universal minimum is $1.";

/** The salon's gift card settings as the form holds them (`""` = blank). */
interface GiftCardFormValues {
  isEnabled: boolean;
  minAmount: number | "";
  maxAmount: number | "";
  defaultExpiryDays: number | "";
}

const EMPTY_VALUES: GiftCardFormValues = {
  isEnabled: false,
  minAmount: UNIVERSAL_MIN_AMOUNT,
  maxAmount: "",
  defaultExpiryDays: "",
};

/**
 * Normalize the backend config into form values. Backend defaults for an
 * unconfigured salon can be 0; start the min at the universal floor and leave
 * max/expiry blank so the staffer sets them.
 */
const normalizeConfig = (config: GiftCardConfig): GiftCardFormValues => ({
  isEnabled: Boolean(config.is_enabled),
  minAmount:
    typeof config.min_amount === "number" &&
    config.min_amount >= UNIVERSAL_MIN_AMOUNT
      ? config.min_amount
      : UNIVERSAL_MIN_AMOUNT,
  maxAmount:
    typeof config.max_amount === "number" && config.max_amount > 0
      ? config.max_amount
      : "",
  defaultExpiryDays:
    typeof config.default_expiry_days === "number"
      ? config.default_expiry_days
      : "",
});

/**
 * Salon-level gift card configuration. Reworked onto the shared Settings design
 * system (SettingsPageLayout + SettingsPageHeader + SettingsSection +
 * SettingsField in a SettingsTabShell read/edit flow) so it matches My Business,
 * Commissions, and the other redesigned Settings surfaces. A single config per
 * salon, so it renders one page of field rows rather than a card grid.
 *
 * The `$1 universal floor` surfaces as help text the moment a staffer sets the
 * minimum below $1, and Save is gated on `min >= 1` and `max >= min`.
 */
const GiftCardSettings = () => {
  const { businessInfo } = useSalon() as SalonContextType;
  const salonId = (businessInfo as BusinessInfo | null)?.id ?? "";
  const perms = usePermissions();
  const giftCardsPerms = perms?.settingsGiftCards;

  const { getGiftCardConfig, updateGiftCardConfig } = useApp();

  const isLargeScreen = useMediaQuery("(min-width: 769px)");

  // `saved` backs the read values; `draft` backs the edit controls and is
  // re-seeded from `saved` on each Edit.
  const [saved, setSaved] = useState<GiftCardFormValues>(EMPTY_VALUES);
  const [draft, setDraft] = useState<GiftCardFormValues>(EMPTY_VALUES);
  const [editing, setEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!salonId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const config: GiftCardConfig = await getGiftCardConfig(salonId);
        if (cancelled) return;
        setSaved(normalizeConfig(config));
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching gift card config:", err);
          setError("Failed to load gift card settings.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [salonId, getGiftCardConfig]);

  // Validation runs off the draft. Narrow to real numbers up front so the
  // comparisons below type-check cleanly.
  const minNum = typeof draft.minAmount === "number" ? draft.minAmount : null;
  const maxNum = typeof draft.maxAmount === "number" ? draft.maxAmount : null;
  // Reactive help text: surface the universal floor the instant min drops below $1.
  const showUniversalFloorHint =
    minNum !== null && minNum < UNIVERSAL_MIN_AMOUNT;
  const maxError =
    maxNum === null
      ? "Enter a maximum amount."
      : minNum !== null && maxNum < minNum
        ? "Maximum must be at least the minimum."
        : null;
  const isValid =
    minNum !== null &&
    minNum >= UNIVERSAL_MIN_AMOUNT &&
    maxNum !== null &&
    maxNum >= minNum;

  const setField = <K extends keyof GiftCardFormValues>(
    field: K,
    value: GiftCardFormValues[K],
  ) => setDraft((prev) => ({ ...prev, [field]: value }));

  const startEdit = () => {
    setDraft(saved);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!isValid || minNum === null || maxNum === null) return;
    setIsSaving(true);
    setError(null);
    try {
      await updateGiftCardConfig(salonId, {
        is_enabled: draft.isEnabled,
        min_amount: minNum,
        max_amount: maxNum,
        default_expiry_days:
          typeof draft.defaultExpiryDays === "number"
            ? draft.defaultExpiryDays
            : null,
      });
      setSaved(draft);
      setEditing(false);
      notifySuccess("Your gift card configuration has been updated.", {
        title: "Gift card settings saved",
      });
    } catch (err) {
      console.error("Error saving gift card config:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save gift card settings. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // On larger screens the edit column is wide; center the switch within it (in
  // edit mode only) so it isn't marooned at the far right — matching the other
  // redesigned Settings tabs.
  const centerSwitch = (node: React.ReactNode) =>
    isLargeScreen ? (
      <Group justify="center" w="100%">
        {node}
      </Group>
    ) : (
      node
    );

  // Hoist the read/edit toggle into the page header (title on the left, Edit on
  // the right). It sits outside the LoadingOverlay Box below, so gate it on a
  // finished load too — otherwise it could enter edit mode before `saved` is
  // populated. An explicit permission deny (canAccess === false) hides it.
  const canEdit = giftCardsPerms?.canAccess !== false;
  const header = (
    <SettingsPageHeader
      title="Gift Cards"
      subtitle={GIFT_CARDS_SUBTITLE}
      actions={
        canEdit && !editing && !isLoading ? (
          <SettingsEditButton onClick={startEdit} />
        ) : undefined
      }
    />
  );

  // Mirror the menu gate: hide only on an explicit deny so an unset (backend
  // not yet returning the section) still renders behind the env flag.
  if (giftCardsPerms?.canAccess === false) {
    return (
      <SettingsPageLayout header={header}>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Access Restricted"
          color="yellow"
        >
          Gift card settings are only available to Owners and Managers.
        </Alert>
      </SettingsPageLayout>
    );
  }

  const expiryRead =
    saved.defaultExpiryDays === ""
      ? "Never expires"
      : `${saved.defaultExpiryDays} days`;

  return (
    <SettingsPageLayout header={header}>
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Box pos="relative" mih={isLoading ? 160 : undefined}>
        <LoadingOverlay visible={isLoading} />

        <SettingsTabShell
          editing={editing}
          saving={isSaving}
          hideEditButton
          onEdit={startEdit}
          onCancel={cancelEdit}
          onSave={handleSave}
          saveDisabled={!isValid}
          saveDisabledReason="Set a minimum of at least $1 and a maximum that's at least the minimum."
        >
          <Stack gap="md">
            <SettingsSection
              title="Availability"
              description="Turn gift cards on to sell and redeem them at your salon."
            >
              <SettingsField
                label="Enable gift cards"
                info="Allow gift cards to be sold and redeemed at your salon."
                editing={editing}
                withDivider={false}
                editControl={centerSwitch(
                  <Switch
                    aria-label="Enable gift cards"
                    checked={draft.isEnabled}
                    onChange={(event) =>
                      setField("isEnabled", event.currentTarget.checked)
                    }
                    color="var(--brand-teal)"
                  />,
                )}
              >
                {saved.isEnabled ? "Enabled" : "Disabled"}
              </SettingsField>
            </SettingsSection>

            <SettingsSection
              title="Sale amounts & expiry"
              description="The value range a gift card can be sold for, and how long it stays valid."
            >
              <SettingsField
                label="Minimum amount"
                info="The smallest value a gift card can be sold for. The universal minimum is $1."
                editing={editing}
                required={editing}
                editControl={
                  <NumberInput
                    aria-label="Minimum amount"
                    value={draft.minAmount}
                    onChange={(value) =>
                      setField(
                        "minAmount",
                        typeof value === "number" ? value : "",
                      )
                    }
                    min={0}
                    decimalScale={2}
                    prefix="$"
                    w="100%"
                    error={showUniversalFloorHint}
                    description={
                      showUniversalFloorHint
                        ? "Universal minimum is $1"
                        : undefined
                    }
                  />
                }
              >
                {formatCurrency(
                  saved.minAmount === "" ? null : saved.minAmount,
                )}
              </SettingsField>

              <SettingsField
                label="Maximum amount"
                info="The largest value a gift card can be sold for."
                editing={editing}
                required={editing}
                editControl={
                  <NumberInput
                    aria-label="Maximum amount"
                    value={draft.maxAmount}
                    onChange={(value) =>
                      setField(
                        "maxAmount",
                        typeof value === "number" ? value : "",
                      )
                    }
                    min={0}
                    decimalScale={2}
                    prefix="$"
                    w="100%"
                    error={maxError}
                  />
                }
              >
                {formatCurrency(
                  saved.maxAmount === "" ? null : saved.maxAmount,
                )}
              </SettingsField>

              <SettingsField
                label="Default expiry (days)"
                info="How long a gift card stays valid after purchase. Leave blank for cards that never expire."
                editing={editing}
                withDivider={false}
                editControl={
                  <NumberInput
                    aria-label="Default expiry (days)"
                    value={draft.defaultExpiryDays}
                    onChange={(value) =>
                      setField(
                        "defaultExpiryDays",
                        typeof value === "number" ? value : "",
                      )
                    }
                    min={1}
                    allowDecimal={false}
                    placeholder="Never expires"
                    w="100%"
                  />
                }
              >
                {expiryRead}
              </SettingsField>
            </SettingsSection>
          </Stack>
        </SettingsTabShell>
      </Box>
    </SettingsPageLayout>
  );
};

export default GiftCardSettings;
